import React, { FC, useCallback, useMemo, useState } from "react";

import { Button, Dropdown, Modal, ModalProps } from "react-bootstrap";

import classNames from "classnames";
import { useResetRecoilState } from "recoil";

import { ParticipantTypes, ResaleStatuses, useUpdateResaleStatusAndArtistForBatchResalesMutation } from "@api/graphql";
import { ToastMessage, type ToastMessageType } from "@components/ToastMessage";
import { ResalesType, selectedResales } from "@states/resale_submission";
import { getDisplayValueFromDBValue } from "@util/index";
import {
  StatusAndReasonOption,
  deSerialiseOption,
  mapResaleStatusReasonsToDisplayFriendlyText,
  optedOutReasonOptions,
  researchRequiredReasonOptions,
  serialiseOption,
} from "@util/resale";

import { ParticipantWithSinglePseudonym } from "./account_profile/ParticipantDropdown";
import ParticipantSearch from "./account_profile/ParticipantSearch";

import styles from "./BatchUpdateResaleStatusAndArtistModal.module.scss";

type ModalSteps = "selectArtist" | "selectResaleStatus";

interface BatchUpdateResaleStatusAndArtistModalProps extends ModalProps {
  resaleData: ResalesType[];
  isOpen: boolean;
  onModalClose: () => void;
  refetch?: () => void;
}

interface ArtistSelectionProps {
  resalesAmount: number;
  onArtistSelectDone: (artist: ParticipantWithSinglePseudonym | null) => void;
}

const ArtistSelection: FC<ArtistSelectionProps> = ({ onArtistSelectDone, resalesAmount }) => {
  const [selectedArtist, setSelectedArtist] = useState<ParticipantWithSinglePseudonym | null>(null);

  const handleParticipantSelect = useCallback((participant: ParticipantWithSinglePseudonym | null) => {
    setSelectedArtist(participant);
  }, []);

  const handleSubmit = useCallback(() => {
    onArtistSelectDone(selectedArtist);
  }, [onArtistSelectDone, selectedArtist]);

  const handleSkip = useCallback(() => {
    onArtistSelectDone(null);
  }, [onArtistSelectDone]);

  return (
    <div className="px-5 py-5">
      <Modal.Header closeButton />
      <Modal.Title className="mb-4 d-flex justify-content-center">Select artist to assign</Modal.Title>
      <Modal.Body>
        <ParticipantSearch
          onParticipantSelect={handleParticipantSelect}
          variables={{
            filter_by: [
              { field: "participantType", eq: ParticipantTypes.ARTIST },
              { field: "filterByParticipantId", eq: "true" },
            ],
          }}
        />
      </Modal.Body>
      <div className="d-flex flex-column justify-content-center align-items-center">
        <div>
          <Button onClick={handleSubmit} disabled={!selectedArtist}>
            Apply to {resalesAmount} reports
          </Button>
        </div>
        <div className={classNames(styles["skip-button"], "mt-4")} onClick={handleSkip}>
          <span>Skip</span>
        </div>
      </div>
    </div>
  );
};

const ResaleStatusDropdown: FC<{
  title?: string;
  onResaleStatusSelected: (selectedResaleStatus: string | null) => void;
}> = ({ title, onResaleStatusSelected }) => {
  return (
    <Dropdown onSelect={onResaleStatusSelected}>
      <Dropdown.Toggle
        id="resale_status_dropdown"
        variant="none"
        className="customized-dropdown-toggle w-100 text-truncate"
      >
        {title ?? "Select an option"}
      </Dropdown.Toggle>

      <Dropdown.Menu>
        {[ResaleStatuses.CONFIRMED_ELIGIBLE, ResaleStatuses.CONFIRMED_INELIGIBLE].map((option) => (
          <Dropdown.Item key={option} eventKey={serialiseOption({ status: option })}>
            {getDisplayValueFromDBValue(option)}
          </Dropdown.Item>
        ))}
        <Dropdown.Header>Research required</Dropdown.Header>
        {researchRequiredReasonOptions.map((option) => (
          <Dropdown.Item
            key={option}
            eventKey={serialiseOption({ status: ResaleStatuses.RESEARCH_REQUIRED, reason: option })}
          >
            {mapResaleStatusReasonsToDisplayFriendlyText(option)}
          </Dropdown.Item>
        ))}
        <Dropdown.Header>Opt out</Dropdown.Header>
        {optedOutReasonOptions.map((option) => (
          <Dropdown.Item key={option} eventKey={serialiseOption({ status: ResaleStatuses.OPTED_OUT, reason: option })}>
            {mapResaleStatusReasonsToDisplayFriendlyText(option)}
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
};

const ResaleStatusSelection: FC<{
  resalesAmount: number;
  onSubmitClicked: (resaleStatus: StatusAndReasonOption) => void;
  updating: boolean;
  validResaleAmount: number;
}> = ({ resalesAmount, onSubmitClicked, updating, validResaleAmount }) => {
  const [selectedResaleStatus, setSelectedResaleStatus] = useState<StatusAndReasonOption | null>(null);

  const titleToDisplay = useMemo(() => {
    if (selectedResaleStatus?.reason) {
      return mapResaleStatusReasonsToDisplayFriendlyText(selectedResaleStatus?.reason);
    }
    if (selectedResaleStatus?.status) {
      return getDisplayValueFromDBValue(selectedResaleStatus?.status);
    }
    return undefined;
  }, [selectedResaleStatus]);

  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastMessageType, setToastMessageType] = useState<ToastMessageType>();

  const handleOnResaleStatusSelected = useCallback(
    (selectedResaleStatus: string | null) => {
      if (!selectedResaleStatus) {
        setSelectedResaleStatus(null);
        return;
      }
      const deSerialisedResaleStatus = deSerialiseOption(selectedResaleStatus);
      if (validResaleAmount < resalesAmount && deSerialisedResaleStatus.status === ResaleStatuses.CONFIRMED_ELIGIBLE) {
        setToastMessage(
          "One or more resales have no existing artist linked to the resale. You are not allowed to update to 'Confirmed Eligible'.",
        );
        setShowToast(true);
        setToastMessageType("error");
        return;
      }
      setSelectedResaleStatus(deSerialisedResaleStatus);
    },
    [validResaleAmount, resalesAmount],
  );

  const handleOnSubmitClicked = useCallback(() => {
    if (selectedResaleStatus) {
      onSubmitClicked(selectedResaleStatus);
    }
  }, [onSubmitClicked, selectedResaleStatus]);

  return (
    <>
      <ToastMessage
        message={toastMessage}
        show={showToast}
        setShow={setShowToast}
        delay={3000}
        type={toastMessageType}
      />
      <div className="px-5 py-5">
        <Modal.Header closeButton />
        <Modal.Title className="mb-4 d-flex justify-content-center">Select status to assign</Modal.Title>
        <Modal.Body>
          <ResaleStatusDropdown title={titleToDisplay} onResaleStatusSelected={handleOnResaleStatusSelected} />
        </Modal.Body>
        <div className="mt-4 d-flex justify-content-center">
          <Button onClick={handleOnSubmitClicked} disabled={!selectedResaleStatus || updating}>
            {updating ? "Updating..." : `Apply to ${resalesAmount} reports`}
          </Button>
        </div>
      </div>
    </>
  );
};

export const BatchUpdateResaleStatusAndArtistModal: FC<BatchUpdateResaleStatusAndArtistModalProps> = ({
  resaleData,
  isOpen,
  onModalClose,
  refetch,
  ...rest
}) => {
  const [currentStep, setCurrentStep] = useState<ModalSteps>("selectArtist");

  const [selectedArtist, setSelectedArtist] = useState<ParticipantWithSinglePseudonym | null>(null);
  const resetSelectedResales = useResetRecoilState(selectedResales);

  const validResales = useMemo(() => {
    if (!selectedArtist) {
      return resaleData.filter((resale) => !!resale.artistId);
    }
    return resaleData;
  }, [resaleData, selectedArtist]);

  const [updateResaleStatusAndArtistForBatchResalesMutation, { loading }] =
    useUpdateResaleStatusAndArtistForBatchResalesMutation();

  const handleOnArtistSelectDone = useCallback((participant: ParticipantWithSinglePseudonym | null) => {
    setSelectedArtist(participant);
    setCurrentStep("selectResaleStatus");
  }, []);

  const handleOnHide = useCallback(() => {
    setSelectedArtist(null);
    setCurrentStep("selectArtist");
    onModalClose();
  }, [onModalClose]);

  const handleSubmitClicked = useCallback(
    async (resaleStatus: StatusAndReasonOption | null) => {
      if (resaleStatus) {
        await updateResaleStatusAndArtistForBatchResalesMutation({
          variables: {
            input: {
              resale_ids: resaleData.map((resale) => resale.resaleId),
              artist_id: selectedArtist?.participant_id,
              resale_status: resaleStatus.status,
              resale_status_reason: resaleStatus.reason,
            },
          },
          onCompleted() {
            setSelectedArtist(null);
            setCurrentStep("selectArtist");
            onModalClose();
            refetch?.();
            resetSelectedResales();
          },
        });
      }
    },
    [
      onModalClose,
      refetch,
      selectedArtist?.participant_id,
      updateResaleStatusAndArtistForBatchResalesMutation,
      resaleData,
      resetSelectedResales,
    ],
  );

  return (
    <Modal show={isOpen} centered size="lg" onHide={handleOnHide} {...rest}>
      {currentStep === "selectArtist" && (
        <ArtistSelection onArtistSelectDone={handleOnArtistSelectDone} resalesAmount={resaleData.length} />
      )}
      {currentStep === "selectResaleStatus" && (
        <ResaleStatusSelection
          onSubmitClicked={handleSubmitClicked}
          updating={loading}
          resalesAmount={resaleData.length}
          validResaleAmount={validResales.length}
        />
      )}
    </Modal>
  );
};
